/**
 * The author half of a message row: the avatar gutter and the name above the
 * first bubble of a run. Both open that person's profile card, which is what
 * Teams does and what makes an avatar in a timeline worth clicking.
 *
 * NO PRESENCE DOT ON A MESSAGE AVATAR — deliberate, and it fixes a bug.
 * `UserRef.presence` is never populated by the API (see USER_REF_COLUMNS), so
 * reading it captioned every message, including your own, "Offline". A message
 * is a record of the past; the author's presence right now belongs to the
 * header, the roster and the profile card, which all subscribe to it live.
 */
import { memo } from 'react';
import { Avatar } from '@cyphex/ui';
import type { UserRef } from '@cyphex/types';
import { ProfileCardPopover } from '@/features/people/components/ProfileCardPopover';
import { formatClockShort } from '../../lib/time';

export interface MessageAvatarGutterProps {
  sender: UserRef | null;
  /** True for a message that continues a run — the gutter holds the time stamp. */
  grouped: boolean;
  /** ISO timestamp, shown in the gutter on hover for a grouped message. */
  createdAt: string;
}

export const MessageAvatarGutter = memo(function MessageAvatarGutter({
  sender,
  grouped,
  createdAt,
}: MessageAvatarGutterProps) {
  return (
    <div className="flex w-8 shrink-0 justify-end">
      {!grouped && sender ? (
        <ProfileCardPopover
          userId={sender.id}
          displayName={sender.displayName}
          avatarUrl={sender.avatarUrl}
          align="start"
        >
          <Avatar name={sender.displayName} src={sender.avatarUrl ?? undefined} size="md" />
        </ProfileCardPopover>
      ) : (
        /* THE GUTTER STAMP. A grouped message carries no header, so its own
           time would otherwise be unrecoverable without opening the row's
           menu. Teams parks it in the empty avatar column and reveals it on
           hover — the information is there when you look for it and costs
           the run nothing when you are just reading. Short form (`1:19`)
           because the gutter is exactly one avatar wide. */
        <time
          dateTime={createdAt}
          className="pt-1 text-2xs tabular-nums text-muted-foreground opacity-0 transition-opacity duration-fast group-hover/message:opacity-100"
        >
          {formatClockShort(createdAt)}
        </time>
      )}
    </div>
  );
});

export interface MessageAuthorNameProps {
  sender: UserRef | null;
}

/** The bold name above the first bubble of a run. */
export const MessageAuthorName = memo(function MessageAuthorName({
  sender,
}: MessageAuthorNameProps) {
  if (sender === null) {
    return <span className="text-md font-semibold leading-4 text-foreground">Unknown</span>;
  }
  return (
    <ProfileCardPopover
      userId={sender.id}
      displayName={sender.displayName}
      avatarUrl={sender.avatarUrl}
      className="hover:underline"
    >
      <span className="text-md font-semibold leading-4 text-foreground">
        {sender.displayName}
      </span>
    </ProfileCardPopover>
  );
});
